import { Component, OnInit } from '@angular/core'
import { AngularRoutes, ChangeLogsEntityEnum, Client, ContractStatus, EnumNames, Subscription, SubscriptionFieldChange, SubscriptionType } from '@parkupp/core'
import { ActivatedRoute } from '@angular/router'
import { SubscriptionService } from 'src/app/services/subscription.service'
import { ClientService } from 'src/app/services/client.service'
import { ParkingService } from 'src/app/services/parking.service'
import { AdminChangeLogService } from 'src/app/services/admin-change-log.service'
import { BackendService } from 'src/app/services/backend.service'

interface SubscriptionEventData {
    timestamp: any
    eventType: string
    description: string
    previousStatus?: string
    newStatus?: string
    actor: string
    actorDetails?: {
        name?: string
        email?: string
        adminKey?: string
        ip?: string
        userAgent?: string
    }
    source?: string
    changes?: SubscriptionFieldChange[]
    metadata?: Record<string, any>
}

@Component({
    selector: 'app-admin-view-subscription',
    templateUrl: './admin-view-subscription.component.html',
    styleUrls: ['./admin-view-subscription.component.scss'],
})
export class AdminViewSubscriptionComponent implements OnInit {
    EnumNames = EnumNames
    AngularRoutes = AngularRoutes
    SubscriptionType = SubscriptionType
    subscriptionKey: string
    subscription: Subscription
    loading: boolean = true
    events: { timestamp: Date; date: string; title: string; description: string; type?: string; actor?: string; changes?: SubscriptionFieldChange[] }[] = []

    // First payment (charge on landlord approval) admin actions
    firstPaymentConfirm: 'retry' | 'cancel' | null = null
    firstPaymentBusy = false
    firstPaymentMessage = ''
    firstPaymentError = false

    constructor(
        private activatedRoute: ActivatedRoute,
        private subscriptionService: SubscriptionService,
        private clientService: ClientService,
        private parkingService: ParkingService,
        private adminChangeLogService: AdminChangeLogService,
        private backendService: BackendService
    ) {}

    /** True when this request has any charge-on-approval first-payment state worth showing */
    get showFirstPaymentPanel(): boolean {
        const s = this.subscription
        if (!s) return false
        return !!(s.chargeOnApproval || s.landlordAcceptedAt || s.firstPaymentAttempts || s.firstPaymentFailedAt)
    }

    get firstPaymentNeedsAttention(): boolean {
        return !!this.subscription?.firstPaymentEscalatedAt && this.subscription.status === ContractStatus.paymentInitiated
    }

    async runFirstPaymentAction() {
        const action = this.firstPaymentConfirm
        if (!action) return
        this.firstPaymentBusy = true
        this.firstPaymentMessage = ''
        try {
            const response =
                action === 'retry'
                    ? await this.backendService.httpAdminRetryFirstPayment(this.subscriptionKey)
                    : await this.backendService.httpAdminCancelFirstPayment(this.subscriptionKey)
            this.firstPaymentError = !!response?.error
            this.firstPaymentMessage = response?.message || (response?.error ? 'Something went wrong.' : 'Done.')
            const { $client, $parking } = this.subscription
            this.subscription = await this.subscriptionService.getPromise(this.subscriptionKey)
            this.subscription.$client = $client
            this.subscription.$parking = $parking
        } catch (error: any) {
            this.firstPaymentError = true
            this.firstPaymentMessage = error?.message || 'Something went wrong.'
        } finally {
            this.firstPaymentBusy = false
            this.firstPaymentConfirm = null
        }
    }

    async ngOnInit() {
        this.subscriptionKey = this.activatedRoute.snapshot.params['subscriptionKey']
        this.loading = true

        try {
            this.subscription = await this.subscriptionService.getPromise(this.subscriptionKey)

            if (this.subscription.clientRef) {
                this.subscription.$client = await this.clientService.getPromise(this.subscription.clientRef.id)
            }
            if (this.subscription.parkingRef) {
                this.subscription.$parking = await this.parkingService.getPromise(this.subscription.parkingRef.id)
            }

            await this.buildTimeline()
        } catch (error) {
            console.error('Error loading subscription details:', error)
        } finally {
            this.loading = false
        }
    }

    private parseTimestamp(ts: any): Date {
        if (!ts) return new Date(0)
        if (ts.toDate) return ts.toDate()
        if (ts.seconds !== undefined) return new Date(ts.seconds * 1000)
        if (typeof ts === 'string' || typeof ts === 'number') return new Date(ts)
        return new Date(0)
    }

    private formatDate(date: Date): string {
        if (!date || isNaN(date.getTime()) || date.getTime() === 0) return 'Unknown date'
        return date.toLocaleString()
    }

    private resolveActor(event: SubscriptionEventData): string {
        if (event.actorDetails?.name && event.actorDetails.name !== 'Admin') {
            return event.actorDetails.name
        }
        return event.actorDetails?.email || event.actorDetails?.adminKey || event.actor
    }

    private async buildTimeline() {
        const subscriptionAny = this.subscription as any
        if (subscriptionAny.eventLog && subscriptionAny.eventLog.length > 0) {
            for (const event of subscriptionAny.eventLog as SubscriptionEventData[]) {
                const eventDate = this.parseTimestamp(event.timestamp)
                this.events.push({
                    timestamp: eventDate,
                    date: this.formatDate(eventDate),
                    title: this.getEventTitle(event.eventType),
                    description: event.description,
                    type: this.getEventTypeClass(event.eventType),
                    actor: this.resolveActor(event),
                    changes: event.changes,
                })
            }
        }

        if (this.events.length === 0) {
            if (this.subscription.createdAt) {
                const createdAt = this.subscription.createdAt.toDate()
                this.events.push({
                    timestamp: createdAt,
                    date: createdAt.toLocaleString(),
                    title: 'Subscription Created',
                    description: `Subscription created for ${this.subscription.$client?.firstName || ''} ${this.subscription.$client?.lastName || ''}`,
                    type: 'created',
                })
            }

            if (this.subscription.contractCreationLog) {
                const contractDate = this.subscription.contractCreationLog.date.toDate()
                this.events.push({
                    timestamp: contractDate,
                    date: contractDate.toLocaleString(),
                    title: 'Contract Signed',
                    description: `IP: ${this.subscription.contractCreationLog.ip}`,
                    type: 'contract',
                    actor: `${this.subscription.contractCreationLog.firstName || ''} ${this.subscription.contractCreationLog.lastName || ''}`.trim(),
                })
            }

            const subscriptionRef = this.subscriptionService.getDocRef(this.subscription.$key!)
            const changeLogs = await this.adminChangeLogService.list(ChangeLogsEntityEnum.SUBSCRIPTION, { subscriptionRef: subscriptionRef as any })
            for (const changeLog of changeLogs) {
                const changeDate = changeLog.createdAt.toDate()
                let adminName = 'Admin'
                if (changeLog.adminRef) {
                    try {
                        const admin = await this.clientService.getPromise(changeLog.adminRef.id)
                        adminName = `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || 'Admin'
                    } catch (e) {}
                }
                this.events.push({
                    timestamp: changeDate,
                    date: changeDate.toLocaleString(),
                    title: 'Admin Update',
                    description: changeLog.comment,
                    type: 'admin',
                    actor: adminName,
                })
            }
        }

        this.events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
    }

    getEventTitle(eventType: string): string {
        const titles: { [key: string]: string } = {
            'created': 'Subscription Created',
            'contract_signed': 'Contract Signed',
            'status_change': 'Status Changed',
            'payment_initiated': 'Awaiting Approval',
            'first_payment_attempted': 'First Payment Attempted',
            'first_payment_succeeded': 'First Payment Succeeded',
            'first_payment_declined': 'First Payment Failed',
            'first_payment_provider_error': 'First Payment Provider Error',
            'first_payment_retry_scheduled': 'First Payment Retry Scheduled',
            'first_payment_escalated': 'First Payment Needs Attention',
            'payment_received': 'Payment Received',
            'payment_failed': 'Payment Failed',
            'merchant_approved': 'Merchant Approved',
            'merchant_rejected': 'Merchant Rejected',
            'merchant_reminder_sent': 'Reminder Sent',
            'client_cancelled': 'Client Cancelled',
            'client_approved': 'Client Approved',
            'client_updated': 'Client Updated',
            'merchant_updated': 'Merchant Updated',
            'admin_updated': 'Admin Updated',
            'admin_cancelled': 'Admin Cancelled',
            'admin_note': 'Admin Note',
            'expired': 'Expired',
            'completed': 'Completed',
            'renewal_initiated': 'Renewal Started',
            'renewal_completed': 'Renewed',
            'void_requested': 'Void Requested',
            'void_completed': 'Void Completed',
            'settlement_requested': 'Settlement Requested',
            'settlement_completed': 'Settlement Completed',
            'email_sent': 'Email Sent',
            'whatsapp_sent': 'WhatsApp Sent',
            'slack_notification': 'Slack Notification',
            'review_requested': 'Review Requested',
            'note': 'Note',
            'other': 'Event',
        }
        return titles[eventType] || eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    }

    getEventTypeClass(eventType: string): string {
        if (['merchant_approved', 'client_approved', 'settlement_completed', 'payment_received', 'first_payment_succeeded'].includes(eventType)) {
            return 'success'
        }
        if (['merchant_rejected', 'client_cancelled', 'expired', 'payment_failed', 'void_completed', 'first_payment_declined', 'first_payment_escalated'].includes(eventType)) {
            return 'danger'
        }
        if (['admin_updated', 'admin_cancelled', 'admin_note', 'client_updated', 'merchant_updated'].includes(eventType)) {
            return 'warning'
        }
        if (['email_sent', 'whatsapp_sent', 'slack_notification'].includes(eventType)) {
            return 'info'
        }
        return 'default'
    }
}
